import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCancelDeepResearchJobMutation,
  useGetDeepResearchJobStatusLazyQuery,
} from "@axio/graphql/generated/graphql";

const DEEP_RESEARCH_POLL_STORAGE_KEY = "companion-active-deep-research-polls";

type DeepResearchPollTarget = {
  streamChatKey: string;
  messageId: string | null;
  startedAtMs: number;
};

type PersistedDeepResearchPollState = {
  activeJobId: string | null;
  targets: Record<string, DeepResearchPollTarget>;
};

const isValidDeepResearchPollTarget = (value: unknown): value is DeepResearchPollTarget => {
  if (value == null || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.streamChatKey === "string" &&
    (typeof candidate.messageId === "string" || candidate.messageId == null) &&
    typeof candidate.startedAtMs === "number" &&
    Number.isFinite(candidate.startedAtMs)
  );
};

const readPersistedDeepResearchPollState = (): PersistedDeepResearchPollState => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return { activeJobId: null, targets: {} };
  }

  try {
    const raw = window.sessionStorage.getItem(DEEP_RESEARCH_POLL_STORAGE_KEY);
    if (!raw) {
      return { activeJobId: null, targets: {} };
    }

    const parsed = JSON.parse(raw) as {
      activeJobId?: unknown;
      targets?: Record<string, unknown>;
    };

    const targets = Object.fromEntries(
      Object.entries(parsed.targets ?? {}).filter(([, value]) => isValidDeepResearchPollTarget(value))
    ) as Record<string, DeepResearchPollTarget>;

    const activeJobId =
      typeof parsed.activeJobId === "string" && targets[parsed.activeJobId] ? parsed.activeJobId : null;

    return {
      activeJobId: activeJobId ?? Object.keys(targets)[0] ?? null,
      targets,
    };
  } catch {
    return { activeJobId: null, targets: {} };
  }
};

const persistDeepResearchPollState = (state: PersistedDeepResearchPollState) => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  if (Object.keys(state.targets).length === 0) {
    window.sessionStorage.removeItem(DEEP_RESEARCH_POLL_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(DEEP_RESEARCH_POLL_STORAGE_KEY, JSON.stringify(state));
};

export function useDeepResearchPolling(args: {
  refetchActiveChat: () => Promise<unknown>;
  /** Refreshes thread list preview so renamed chats (e.g. after deep research) show the new title in the sidebar. */
  refetchPreviewChats?: () => Promise<unknown>;
  /** Run before refetch so server conversation can replace local stream-owned messages (e.g. clear hasStreamedRef). */
  onBeforeDeepResearchRefetch?: () => void;
  onHeartbeat?: (elapsedSeconds: number) => void;
  onTerminal?: (outcome: {
    status: "completed" | "failed" | "cancelled" | "forbidden" | "not_found" | "error";
    message?: string | null;
    streamChatKey?: string;
    messageId?: string | null;
  }) => void;
}) {
  const { refetchActiveChat, refetchPreviewChats, onBeforeDeepResearchRefetch, onHeartbeat, onTerminal } = args;

  const [fetchDeepResearchJobStatus] = useGetDeepResearchJobStatusLazyQuery();
  const [cancelDeepResearchJob] = useCancelDeepResearchJobMutation();

  const pollTargetsRef = useRef<Record<string, DeepResearchPollTarget>>({});
  const [pollVersion, setPollVersion] = useState(0);

  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const commitPollState = useCallback(
    (targets: Record<string, DeepResearchPollTarget>, nextActiveJobId: string | null) => {
      pollTargetsRef.current = targets;
      persistDeepResearchPollState({ targets, activeJobId: nextActiveJobId });
      setActiveJobId(nextActiveJobId);
      setPollVersion((prev) => prev + 1);
    },
    []
  );

  useEffect(() => {
    const persisted = readPersistedDeepResearchPollState();
    if (Object.keys(persisted.targets).length === 0) return;

    pollTargetsRef.current = persisted.targets;
    setActiveJobId(persisted.activeJobId);
    setPollVersion((prev) => prev + 1);
  }, []);

  const stopDeepResearchPolling = useCallback(
    (jobId?: string) => {
      if (jobId) {
        if (!pollTargetsRef.current[jobId]) return;
        const rest = { ...pollTargetsRef.current };
        delete rest[jobId];
        const nextActiveJobId = activeJobId === jobId ? (Object.keys(rest)[0] ?? null) : activeJobId;
        commitPollState(rest, nextActiveJobId);
        return;
      }
      if (Object.keys(pollTargetsRef.current).length === 0) return;
      commitPollState({}, null);
    },
    [activeJobId, commitPollState]
  );

  const startDeepResearchPolling = useCallback(
    (jobId: string, streamChatKey: string, messageId: string | null) => {
      commitPollState(
        {
          ...pollTargetsRef.current,
          [jobId]: {
            streamChatKey,
            messageId,
            startedAtMs: Date.now(),
          },
        },
        jobId
      );
    },
    [commitPollState]
  );

  const remapDeepResearchPollMessageId = useCallback(
    (oldMessageId: string, newMessageId: string) => {
      const next: Record<string, DeepResearchPollTarget> = {};
      let changed = false;
      for (const [jobId, target] of Object.entries(pollTargetsRef.current)) {
        if (target.messageId === oldMessageId) {
          next[jobId] = { ...target, messageId: newMessageId };
          changed = true;
        } else {
          next[jobId] = target;
        }
      }
      if (changed) {
        commitPollState(next, activeJobId);
      }
    },
    [activeJobId, commitPollState]
  );

  const remapDeepResearchPollChatKey = useCallback(
    (oldChatKey: string, newChatKey: string) => {
      const next: Record<string, DeepResearchPollTarget> = {};
      let changed = false;
      for (const [jobId, target] of Object.entries(pollTargetsRef.current)) {
        if (target.streamChatKey === oldChatKey) {
          next[jobId] = { ...target, streamChatKey: newChatKey };
          changed = true;
        } else {
          next[jobId] = target;
        }
      }
      if (changed) {
        commitPollState(next, activeJobId);
      }
    },
    [activeJobId, commitPollState]
  );

  const cancelActiveDeepResearch = useCallback(async () => {
    if (!activeJobId) return;
    try {
      await cancelDeepResearchJob({ variables: { jobId: activeJobId } });
    } finally {
      stopDeepResearchPolling(activeJobId);
      onBeforeDeepResearchRefetchRef.current?.();
      onTerminal?.({ status: "cancelled" });
      void refetchActiveChat();
    }
  }, [activeJobId, cancelDeepResearchJob, onTerminal, refetchActiveChat, stopDeepResearchPolling]);

  /** Parent often passes inline callbacks; if they are effect deps, every re-render re-runs the effect and fires an immediate poll (storm). */
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;
  const onBeforeDeepResearchRefetchRef = useRef(onBeforeDeepResearchRefetch);
  onBeforeDeepResearchRefetchRef.current = onBeforeDeepResearchRefetch;
  const refetchActiveChatRef = useRef(refetchActiveChat);
  refetchActiveChatRef.current = refetchActiveChat;
  const refetchPreviewChatsRef = useRef(refetchPreviewChats);
  refetchPreviewChatsRef.current = refetchPreviewChats;
  const stopDeepResearchPollingRef = useRef(stopDeepResearchPolling);
  stopDeepResearchPollingRef.current = stopDeepResearchPolling;

  useEffect(() => {
    const pollJob = async (jobId: string, target: DeepResearchPollTarget) => {
      try {
        const { data } = await fetchDeepResearchJobStatus({
          variables: { jobId },
          fetchPolicy: "network-only",
        });
        if (!pollTargetsRef.current[jobId]) return;
        const payload = data?.getDeepResearchJobStatus;
        if (!payload) return;

        const status = payload.status;
        if (status === "in_progress") return;

        if (status === "completed") {
          const target = pollTargetsRef.current[jobId];
          stopDeepResearchPollingRef.current(jobId);
          onBeforeDeepResearchRefetchRef.current?.();
          void refetchActiveChatRef
            .current()
            .then(() => refetchPreviewChatsRef.current?.())
            .then(() => {
              onTerminalRef.current?.({
                status: "completed",
                streamChatKey: target?.streamChatKey,
                messageId: target?.messageId ?? null,
              });
            });
          return;
        }

        if (
          status === "cancelled" ||
          status === "failed" ||
          status === "forbidden" ||
          status === "not_found" ||
          status === "error"
        ) {
          stopDeepResearchPollingRef.current(jobId);
          onBeforeDeepResearchRefetchRef.current?.();
          onTerminalRef.current?.({
            status: status as "cancelled" | "failed" | "forbidden" | "not_found" | "error",
            message: payload.message,
          });
          void refetchActiveChatRef.current();
          return;
        }

        stopDeepResearchPollingRef.current(jobId);
        onBeforeDeepResearchRefetchRef.current?.();
        onTerminalRef.current?.({ status: "error", message: "Unexpected deep research job status." });
        void refetchActiveChatRef.current();
      } catch (error) {
        console.error("[useDeepResearchPolling] Error polling deep research job", { jobId, error });
        stopDeepResearchPollingRef.current(jobId);
        onBeforeDeepResearchRefetchRef.current?.();
        onTerminalRef.current?.({ status: "error", message: "Failed to fetch deep research status." });
        void refetchActiveChatRef.current();
      }
    };

    const pollAllJobs = () => {
      for (const [jobId, target] of Object.entries(pollTargetsRef.current)) {
        void pollJob(jobId, target);
      }
    };

    if (Object.keys(pollTargetsRef.current).length === 0) return;
    pollAllJobs();
    const interval = setInterval(pollAllJobs, 2500);
    return () => clearInterval(interval);
  }, [fetchDeepResearchJobStatus, pollVersion]);

  const onHeartbeatRef = useRef(onHeartbeat);
  onHeartbeatRef.current = onHeartbeat;

  const activeDeepResearchPollTarget = activeJobId ? (pollTargetsRef.current[activeJobId] ?? null) : null;

  useEffect(() => {
    if (Object.keys(pollTargetsRef.current).length === 0) return;

    const interval = setInterval(() => {
      const first = Object.values(pollTargetsRef.current)[0];
      if (!first) return;
      const elapsedSeconds = Math.max(0, Math.round((Date.now() - first.startedAtMs) / 1000));
      onHeartbeatRef.current?.(elapsedSeconds);
    }, 1000);
    return () => clearInterval(interval);
  }, [pollVersion]);

  return {
    startDeepResearchPolling,
    stopDeepResearchPolling,
    remapDeepResearchPollMessageId,
    remapDeepResearchPollChatKey,
    cancelActiveDeepResearch,
    activeDeepResearchPollTarget,
    activeJobId,
    isDeepResearchPolling: Object.keys(pollTargetsRef.current).length > 0,
  };
}
